// Imported from the registry modules, NOT the `@query` barrel — this list is
// consumed by in-barrel registries, so a barrel import here closes a module
// cycle (fatal under jest.mock's requireActual re-entry).
//
// Exposed as a FUNCTION, not a constant array: the v3 data-source registries
// both import this module (their writes fan out into requirement reads) and
// are referenced by it (their read keys are part of the fan-out). Deferring
// the dereference to call time keeps that cycle safe in any module-init
// order; registries consume it inside lazy `dependencies: () => [...]`
// thunks, so the keys are only read at invalidation time.

import { WSQueryKey } from "@ws-react-query";

import { QUERY_PAYER_ROWS_ALL } from "../../query/search/payer/queries/useQueryPayerRowsAll";
import { queriesAuditLogs } from "../../v3/query/queriesAuditLogs";
import { queriesBackgroundChecks } from "../../v3/query/queriesBackgroundChecks";
import { queriesBiometricIdentityVerificationRequests } from "../../v3/query/queriesBiometricIdentityVerificationRequests";
import { queriesInsuranceCoverages } from "../../v3/query/queriesInsuranceCoverages";
import { queriesInsuranceMonitors } from "../../v3/query/queriesInsuranceMonitors";
import { queriesNotifications } from "../../v3/query/queriesNotifications";
import { queriesPayeeEngagements } from "../../v3/query/queriesPayeeEngagements";
import { queriesPayeeRows } from "../../v3/query/queriesPayeeRows";
import { queriesPayerEngagements } from "../../v3/query/queriesPayerEngagements";
import { queriesRequirements } from "../../v3/query/queriesRequirements";
import { queriesSharedFileRequests } from "../../v3/query/queriesSharedFileRequests";
import { queriesSignatureRequests } from "../../v3/query/queriesSignatureRequests";
import { QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS } from "../Onboarding/queries/useQueryAllPayersWithEngagements";

/**
 * Every read a requirement-instance change propagates into: the five
 * data-source reads, the instance reads themselves, and the surrounding
 * payer/payee/engagement/notification surfaces.
 */
export const getRequirementsDependencies = (): WSQueryKey[] => [
  queriesSignatureRequests.getSignatureRequest.queryKeyBase,
  queriesSharedFileRequests.getSharedFileRequest.queryKeyBase,
  queriesBackgroundChecks.getBackgroundCheck.queryKeyBase,
  queriesBiometricIdentityVerificationRequests
    .getBiometricIdentityVerificationRequest.queryKeyBase,
  queriesInsuranceMonitors.getInsuranceMonitor.queryKeyBase,
  queriesNotifications.listNotifications.queryKeyBase,
  queriesRequirements.listPayeeRequirements.queryKeyBase,
  queriesRequirements.getPayeeRequirement.queryKeyBase,
  QUERY_PAYER_ROWS_ALL,
  queriesPayeeRows.searchPayeeRows.queryKeyBase,
  queriesPayerEngagements.getPayerEngagement.queryKeyBase,
  queriesPayerEngagements.listPayerEngagements.queryKeyBase,
  QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS,
  queriesPayeeEngagements.listPayeeEngagements.queryKeyBase,
  queriesPayeeEngagements.getPayeeEngagement.queryKeyBase,
  queriesAuditLogs.listAuditLogs.queryKeyBase,
  queriesInsuranceCoverages.listInsuranceCoverages.queryKeyBase
];
